import type { AppEvent, HabitRecord } from '../core/types.js';
import { totalSkippedPurchase } from './money.js';

export interface SummaryInput {
  habit: HabitRecord;
  events: AppEvent[];
  /** Inclusive ISO bound; defaults to last 7 days. */
  fromIso?: string;
  toIso?: string;
  /** Privacy redaction: replace habit label with "Plan". */
  redact?: boolean;
}

/**
 * Generate a plain-text weekly summary the user can copy out. Designed for
 * accountability partners — no internal event names, no raw counts of misses
 * unless balanced by wins. Privacy redaction swaps the habit label.
 */
export function buildWeeklySummary(input: SummaryInput): string {
  const now = new Date();
  const from = input.fromIso ?? new Date(now.getTime() - 7 * 86400000).toISOString();
  const to = input.toIso ?? now.toISOString();
  const active = input.events
    .filter((e) => e.status !== 'undone')
    .filter((e) => e.occurredAt >= from && e.occurredAt <= to);

  const label = input.redact ? 'Plan' : (input.habit.privateLabel ?? input.habit.label);
  const lines: string[] = [];
  lines.push(`Weekly summary · ${label}`);
  lines.push(`Range: ${from.slice(0, 10)} to ${to.slice(0, 10)}`);
  lines.push('');

  const uses = active.filter((e) => e.type === 'usage_logged').length;
  const passed = active.filter((e) =>
    e.type === 'success_logged' && (e.payload as { kind?: string }).kind === 'urge_passed').length;
  const waited = active.filter((e) =>
    e.type === 'success_logged' && (e.payload as { kind?: string }).kind === 'waited').length;
  const rescueCompletes = active.filter((e) => e.type === 'rescue_completed').length;
  const skipped = totalSkippedPurchase(active, from);

  lines.push(`Urges beaten: ${passed}`);
  lines.push(`Waits logged: ${waited}`);
  lines.push(`Rescues completed: ${rescueCompletes}`);
  lines.push(`Uses logged: ${uses}`);
  if (skipped.count > 0) {
    lines.push(`Money protected: ${skipped.currency} ${skipped.totalProtected.toFixed(2)} across ${skipped.count} skipped purchases`);
  }

  const waitedThenUsed = active.filter((e) =>
    e.type === 'usage_logged'
    && typeof (e.payload as { waitedMsBeforeUse?: number }).waitedMsBeforeUse === 'number'
    && ((e.payload as { waitedMsBeforeUse: number }).waitedMsBeforeUse) >= 60_000).length;
  if (waitedThenUsed > 0) {
    lines.push(`Waited before using: ${waitedThenUsed} time${waitedThenUsed === 1 ? '' : 's'}`);
  }

  lines.push('');
  lines.push('Generated locally by Wean. No data left this device unless you shared it.');
  return lines.join('\n');
}
